import { useRef, useState, useEffect } from "react";
import { toast } from "sonner";
import { Mic, Square, Upload, Sparkles, Loader2 } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { PlainSelect, Field } from "./Common";
import { LANGS } from "../lib/langs";

function useRecorder() {
  const [rec, setRec] = useState(null);
  const [secs, setSecs] = useState(0);
  const chunks = useRef([]);
  useEffect(() => {
    if (!rec) return undefined;
    const h = setInterval(() => setSecs((s) => s + 1), 1000);
    return () => clearInterval(h);
  }, [rec]);
  const start = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "audio/mp4";
    const r = new MediaRecorder(stream, { mimeType: mime, audioBitsPerSecond: 32000 });
    chunks.current = [];
    r.ondataavailable = (e) => e.data.size && chunks.current.push(e.data);
    r.start(1000);
    setSecs(0); setRec(r);
  };
  const stop = () => new Promise((resolve) => {
    rec.onstop = () => {
      rec.stream.getTracks().forEach((t) => t.stop());
      const type = rec.mimeType.includes("webm") ? "webm" : "m4a";
      resolve(new File(chunks.current, `consultation.${type}`, { type: rec.mimeType }));
      setRec(null);
    };
    rec.stop();
  });
  return { recording: !!rec, secs, start, stop };
}

export default function ScribeDialog({ open, onOpenChange, client, templates, onResult }) {
  const [consent, setConsent] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [tpl, setTpl] = useState("osteo_soap");
  const [extra, setExtra] = useState("");
  const [lang, setLang] = useState("en");
  const [busy, setBusy] = useState("");
  const r = useRecorder();
  const fileRef = useRef(null);

  const transcribe = async (file) => {
    setBusy("Transcribing…");
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("client_id", client.id); fd.append("consent", "true"); fd.append("language", lang);
      const { data } = await api.post("/scribe/transcribe", fd, { timeout: 600000 });
      setTranscript((t) => (t ? `${t}\n${data.text}` : data.text));
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const generate = async () => {
    setBusy("Writing the note…");
    try {
      const { data } = await api.post("/scribe/generate", { client_id: client.id, template_id: tpl, transcript, extra, language: lang }, { timeout: 180000 });
      onResult({ template: templates.find((t) => t.id === tpl), values: data.values, ai: true });
      onOpenChange(false); setTranscript(""); setExtra(""); setConsent(false);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const mm = `${String(Math.floor(r.secs / 60)).padStart(2, "0")}:${String(r.secs % 60).padStart(2, "0")}`;

  return (
    <Dialog open={open} onOpenChange={(o) => !r.recording && onOpenChange(o)}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="scribe-dialog">
        <DialogHeader><DialogTitle>AI Scribe · {client.first_name} {client.last_name}</DialogTitle></DialogHeader>
        <label className="flex items-start gap-2 rounded-lg border p-3 text-sm bg-muted/40">
          <input type="checkbox" className="mt-1" data-testid="scribe-consent-checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} disabled={r.recording} />
          <span>The patient has agreed that this consultation is recorded and transcribed by AI to help write their clinical notes. Consent will be logged with date, time and your name.</span>
        </label>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Language spoken"><PlainSelect data-testid="scribe-lang-select" value={lang} onChange={(e) => setLang(e.target.value)}>
            {Object.entries(LANGS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</PlainSelect></Field>
          <Field label="Note type"><PlainSelect data-testid="scribe-template-select" value={tpl} onChange={(e) => setTpl(e.target.value)}>
            {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}</PlainSelect></Field>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {!r.recording ? (
            <Button className="rounded-full" data-testid="scribe-record-btn" disabled={!consent || !!busy} onClick={() => r.start().catch(() => toast.error("Microphone access was blocked"))}><Mic className="h-4 w-4 mr-1.5" />Start recording</Button>
          ) : (
            <Button variant="destructive" className="rounded-full" data-testid="scribe-stop-btn" onClick={async () => transcribe(await r.stop())}><Square className="h-4 w-4 mr-1.5" />Stop · {mm}</Button>
          )}
          <Button variant="outline" className="rounded-full" data-testid="scribe-upload-btn" disabled={!consent || r.recording || !!busy} onClick={() => fileRef.current.click()}><Upload className="h-4 w-4 mr-1.5" />Upload audio</Button>
          <input ref={fileRef} type="file" accept="audio/*,.m4a,.mp3,.wav,.webm" className="hidden" data-testid="scribe-file-input" onChange={(e) => e.target.files[0] && transcribe(e.target.files[0])} />
          {r.recording && <span className="flex items-center gap-2 text-sm text-destructive"><span className="h-2.5 w-2.5 rounded-full bg-destructive animate-pulse" />Recording</span>}
          {busy && <span className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="scribe-busy"><Loader2 className="h-4 w-4 animate-spin" />{busy}</span>}
        </div>
        <Field label="Transcript (you can edit or paste text)"><Textarea data-testid="scribe-transcript" rows={8} value={transcript} onChange={(e) => setTranscript(e.target.value)} placeholder="The transcript appears here after recording or upload…" /></Field>
        <Field label="Your extra notes for the AI (optional)"><Textarea data-testid="scribe-extra" rows={2} value={extra} onChange={(e) => setExtra(e.target.value)} placeholder="e.g. L4/L5 restriction, treated with HVT, 2 week follow-up" /></Field>
        <Button className="rounded-full w-full" data-testid="scribe-generate-btn" disabled={transcript.trim().length < 10 || !!busy || r.recording} onClick={generate}><Sparkles className="h-4 w-4 mr-1.5" />Write note with AI</Button>
        <p className="text-xs text-muted-foreground">The audio is only used for transcription and is not stored. Always review the AI note before saving.</p>
      </DialogContent>
    </Dialog>
  );
}
